import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Lock, Plus, Trash2, Save, LogOut, Mic, Music as MusicIcon, HandHeart, BarChart3, Users, Calendar, ShoppingBag, Headphones, Download, Mail, Sparkles, ImagePlus, RotateCcw, Clock, CalendarCheck } from "lucide-react";
import { BookingsAdmin } from "../components/admin/BookingsAdmin";
import { DownloadsAdmin } from "../components/admin/DownloadsAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "her_admin_key";

const emptyEpisode = {
    number: 1,
    title: "",
    description: "",
    theme: "Becoming",
    duration: "",
    audio_url: "",
    cover_url: "",
    status: "coming_soon",
    release_at: "",
};

// Convert an ISO datetime string to a `YYYY-MM-DD` value for a date input.
// Episodes drop at 12:00 AM Eastern (04:00 UTC), so we can slice safely.
const isoToDateInput = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
};

// Convert a date input value ("2026-07-07") to a full ISO drop time.
// Fixes the drop moment at 12:00 AM Eastern (04:00 UTC during EDT).
const dateInputToIso = (val) => (val ? `${val}T04:00:00+00:00` : "");

const emptyTrack = {
    title: "",
    description: "",
    cover_url: "",
    apple_url: "",
    spotify_url: "",
    youtube_url: "",
    track_type: "Album",
    order: 1,
};

export const AdminPage = () => {
    const [adminKey, setAdminKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
    const [authed, setAuthed] = useState(!!localStorage.getItem(STORAGE_KEY));
    const [pass, setPass] = useState("");
    const [tab, setTab] = useState("dashboard");

    const headers = { "X-Admin-Key": adminKey };

    const login = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/admin/login`, { key: pass });
            localStorage.setItem(STORAGE_KEY, pass);
            setAdminKey(pass);
            setAuthed(true);
            toast.success("Welcome back, Coach.");
        } catch {
            toast.error("Invalid passcode.");
        }
    };

    const logout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setAdminKey("");
        setAuthed(false);
    };

    if (!authed) {
        return (
            <div className="min-h-screen grid place-items-center bg-gradient-to-br from-lilac-100 to-[#F4ECF7] px-6">
                <Toaster position="top-center" />
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-md bg-white border border-border rounded-3xl p-10 shadow-[0_24px_64px_rgba(43,29,53,0.10)]"
                >
                    <div className="flex items-center gap-3">
                        <span className="grid place-items-center w-12 h-12 rounded-full bg-gold text-white">
                            <Lock className="w-5 h-5" />
                        </span>
                        <div>
                            <div className="eyebrow text-gold-deep">Admin</div>
                            <h1 className="font-display text-2xl text-lilac-900">Becoming H.E.R.</h1>
                        </div>
                    </div>
                    <p className="mt-6 text-sm text-lilac-700 leading-relaxed">
                        Enter your passcode to manage episodes &amp; healing music.
                    </p>
                    <div className="mt-6">
                        <label className="eyebrow text-lilac-700">Passcode</label>
                        <Input
                            type="password"
                            value={pass}
                            onChange={(e) => setPass(e.target.value)}
                            data-testid="admin-passcode"
                            className="mt-2 h-11 rounded-full px-5 border-lilac-300"
                            placeholder="••••••••"
                            autoFocus
                        />
                    </div>
                    <Button
                        type="submit"
                        data-testid="admin-login-submit"
                        className="mt-6 w-full rounded-full h-11 bg-lilac-900 hover:bg-lilac-700 text-white"
                    >
                        Enter
                    </Button>
                    <a href="/" className="mt-6 block text-center text-xs text-lilac-700 hover:text-gold-deep">
                        ← Back to site
                    </a>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F4ECF7] pb-24">
            <Toaster position="top-center" />
            <header className="sticky top-0 z-30 glass border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="grid place-items-center w-9 h-9 rounded-full bg-gold text-white font-display">
                            H
                        </span>
                        <div>
                            <div className="eyebrow text-gold-deep">Admin</div>
                            <div className="font-display text-lg text-lilac-900 leading-none">
                                {tab === "music" ? "Music Studio" : tab === "prayers" ? "Prayer Wall" : tab === "dashboard" ? "Ministry Dashboard" : tab === "bookings" ? "Speaker Bookings" : tab === "downloads" ? "Downloads Activity" : "Episode Studio"}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/"
                            className="hidden sm:inline-flex eyebrow text-lilac-700 hover:text-gold-deep px-4 py-2"
                        >
                            View Site
                        </a>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-1.5 eyebrow text-lilac-900 px-4 py-2 rounded-full border border-border hover:bg-lilac-50"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </div>
                {/* Tabs */}
                <div className="max-w-6xl mx-auto px-6 pb-3 flex gap-2 flex-wrap">
                    <button
                        onClick={() => setTab("dashboard")}
                        data-testid="admin-tab-dashboard"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "dashboard"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <BarChart3 className="w-3.5 h-3.5" /> Dashboard
                    </button>
                    <button
                        onClick={() => setTab("episodes")}
                        data-testid="admin-tab-episodes"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "episodes"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <Mic className="w-3.5 h-3.5" /> Episodes
                    </button>
                    <button
                        onClick={() => setTab("music")}
                        data-testid="admin-tab-music"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "music"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <MusicIcon className="w-3.5 h-3.5" /> Music
                    </button>
                    <button
                        onClick={() => setTab("prayers")}
                        data-testid="admin-tab-prayers"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "prayers"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <HandHeart className="w-3.5 h-3.5" /> Prayers
                    </button>
                    <button
                        onClick={() => setTab("subscribers")}
                        data-testid="admin-tab-subscribers"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "subscribers"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <Mail className="w-3.5 h-3.5" /> Subscribers
                    </button>
                    <button
                        onClick={() => setTab("bookings")}
                        data-testid="admin-tab-bookings"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "bookings"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <CalendarCheck className="w-3.5 h-3.5" /> Bookings
                    </button>
                    <button
                        onClick={() => setTab("downloads")}
                        data-testid="admin-tab-downloads"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            tab === "downloads"
                                ? "bg-lilac-900 text-white"
                                : "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                        }`}
                    >
                        <Download className="w-3.5 h-3.5" /> Downloads
                    </button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 mt-10">
                {tab === "dashboard" ? (
                    <Dashboard headers={headers} onUnauth={logout} />
                ) : tab === "episodes" ? (
                    <EpisodesAdmin headers={headers} adminKey={adminKey} onUnauth={logout} />
                ) : tab === "music" ? (
                    <MusicAdmin headers={headers} onUnauth={logout} />
                ) : tab === "subscribers" ? (
                    <SubscribersAdmin headers={headers} adminKey={adminKey} onUnauth={logout} />
                ) : tab === "bookings" ? (
                    <BookingsAdmin headers={headers} onUnauth={logout} />
                ) : tab === "downloads" ? (
                    <DownloadsAdmin headers={headers} onUnauth={logout} />
                ) : (
                    <PrayersAdmin headers={headers} onUnauth={logout} />
                )}
            </main>
        </div>
    );
};

// ----------------------------- Dashboard -------------------------------- //
const Dashboard = ({ headers, onUnauth }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [savingWelcome, setSavingWelcome] = useState(false);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            axios.get(`${API}/admin/dashboard`, { headers }).catch((err) => {
                if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
                return null;
            }),
            axios.get(`${API}/settings`).catch(() => null),
        ]).then(([dash, sets]) => {
            if (!mounted) return;
            if (dash?.data) setData(dash.data);
            if (sets?.data) setSettings(sets.data);
            setLoading(false);
        });
        return () => { mounted = false; };
    }, []);

    const saveWelcome = async (e) => {
        e.preventDefault();
        if (!settings) return;
        setSavingWelcome(true);
        try {
            const r = await axios.patch(`${API}/admin/settings`, {
                welcome_audio_url: settings.welcome_audio_url || "",
                welcome_title: settings.welcome_title || "",
                welcome_subtitle: settings.welcome_subtitle || "",
                welcome_duration: settings.welcome_duration || "",
            }, { headers });
            setSettings(r.data);
            toast.success("Welcome note saved.");
        } catch (err) {
            if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
            else toast.error("Couldn't save.");
        } finally {
            setSavingWelcome(false);
        }
    };

    if (loading) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-32 rounded-3xl bg-lilac-100/50 animate-pulse" />
                ))}
            </div>
        );
    }
    if (!data) return null;

    const cards = [
        { label: "Waitlist Total", value: data.waitlist_total, hint: "shown on hero", icon: Users, accent: "gold" },
        { label: "Real Subscribers", value: data.subscribers, hint: "newsletter signups", icon: Users },
        { label: "Prayers Submitted", value: data.prayers, hint: "the wall", icon: HandHeart, accent: "gold" },
        { label: "Episodes", value: `${data.episodes_published} / ${data.episodes_total}`, hint: "published / total", icon: Mic },
        { label: "Tracks", value: data.tracks, hint: "in healing music", icon: MusicIcon },
        { label: "Discovery Call Clicks", value: data.events?.discovery_call_clicked || 0, hint: "booking CTA", icon: Calendar, accent: "gold" },
        { label: "Merch Section Clicks", value: data.events?.merch_clicked || 0, hint: "main CTA", icon: ShoppingBag },
        { label: "Episode-Merch Clicks", value: data.events?.episode_merch_clicked || 0, hint: "in-card strips", icon: Headphones },
    ];

    return (
        <div className="space-y-10">
            <div>
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="eyebrow text-gold-deep">Becoming H.E.R.</div>
                        <h2 className="font-display text-3xl text-lilac-900 mt-1">Ministry dashboard</h2>
                    </div>
                    <span className="text-xs text-lilac-700">Live · auto-refresh on visit</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-cards">
                    {cards.map((c) => (
                        <div
                            key={c.label}
                            data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-3xl bg-white border border-border p-5 flex flex-col justify-between min-h-[140px]"
                        >
                            <div className="flex items-center justify-between">
                                <c.icon className={`w-5 h-5 ${c.accent === "gold" ? "text-gold-deep" : "text-lilac-700"}`} />
                                <span className="eyebrow text-lilac-700/80">{c.hint}</span>
                            </div>
                            <div>
                                <div className={`font-sans font-semibold text-4xl tabular-nums leading-none ${c.accent === "gold" ? "text-gold-deep" : "text-lilac-900"}`}>
                                    {c.value}
                                </div>
                                <div className="mt-1.5 text-xs uppercase tracking-wider text-lilac-700">
                                    {c.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Welcome voice memo */}
            {settings && (
                <form
                    onSubmit={saveWelcome}
                    data-testid="welcome-settings-form"
                    className="rounded-3xl bg-white border border-border p-7"
                >
                    <div className="flex items-center gap-2 eyebrow text-gold-deep mb-4">
                        <Mic className="w-3.5 h-3.5" /> Welcome voice memo
                    </div>
                    <h3 className="font-display text-2xl text-lilac-900">
                        Record once, greet every visitor.
                    </h3>
                    <p className="text-sm text-lilac-700 mt-2 max-w-xl leading-relaxed">
                        Drop a 60–90 second voice memo here (any phone recording works — host on
                        Buzzsprout / Anchor / your domain / a public file URL). When you save, a soft
                        "Press Play" card appears on the home page just below the hero. Leave the URL
                        blank to hide the card.
                    </p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="eyebrow text-lilac-700">Audio URL</label>
                            <Input
                                value={settings.welcome_audio_url || ""}
                                onChange={(e) => setSettings({ ...settings, welcome_audio_url: e.target.value })}
                                data-testid="welcome-input-url"
                                className="mt-2 h-10 rounded-xl"
                                placeholder="https://… (mp3, m4a, wav)"
                            />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Title</label>
                            <Input
                                value={settings.welcome_title || ""}
                                onChange={(e) => setSettings({ ...settings, welcome_title: e.target.value })}
                                data-testid="welcome-input-title"
                                className="mt-2 h-10 rounded-xl"
                                placeholder="A note before you go any further"
                            />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Subtitle</label>
                            <Input
                                value={settings.welcome_subtitle || ""}
                                onChange={(e) => setSettings({ ...settings, welcome_subtitle: e.target.value })}
                                data-testid="welcome-input-subtitle"
                                className="mt-2 h-10 rounded-xl"
                                placeholder="Press play. From me to you."
                            />
                        </div>
                    </div>
                    <div className="mt-6">
                        <Button
                            type="submit"
                            disabled={savingWelcome}
                            data-testid="welcome-save"
                            className="rounded-full h-11 px-6 bg-gold hover:bg-gold-deep text-white"
                        >
                            <Save className="w-4 h-4" /> {savingWelcome ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};

// ------------------------- Cover Art Uploader --------------------------- //
// Uploads a new cover-art image and applies it across the site without a
// redeploy. Backed by GET/POST /api/covers via GridFS.
const CoverArtUploader = ({ headers, onDone, onUnauth }) => {
    const [scope, setScope] = useState("all");
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [history, setHistory] = useState([]);
    const [applyingId, setApplyingId] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [schedCoverId, setSchedCoverId] = useState("");
    const [schedWhen, setSchedWhen] = useState("");
    const [schedSaving, setSchedSaving] = useState(false);
    const inputRef = useRef(null);

    const scopeLabel = {
        all: "every episode + every music track",
        podcast: "every episode only",
        music: "every music track only",
    }[scope];

    const loadHistory = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/covers?limit=5`, { headers });
            setHistory(data?.items || []);
        } catch (err) {
            if (err?.response?.status === 401) onUnauth && onUnauth();
        }
    };

    const loadSchedules = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/covers/schedule?status=pending`, { headers });
            setSchedules(data?.items || []);
        } catch (err) {
            if (err?.response?.status === 401) onUnauth && onUnauth();
        }
    };

    useEffect(() => {
        loadHistory();
        loadSchedules();
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    const chooseFile = () => inputRef.current?.click();

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking the same file
        if (!file) return;
        if (!/\.(png|jpg|jpeg|webp)$/i.test(file.name)) {
            toast.error("Choose a PNG, JPG, or WebP image.");
            return;
        }
        if (file.size > 12 * 1024 * 1024) {
            toast.error("Image is over 12 MB. Please compress and try again.");
            return;
        }
        setPreview(URL.createObjectURL(file));
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await axios.post(
                `${API}/admin/covers/upload?scope=${scope}`,
                form,
                { headers: { ...headers, "Content-Type": "multipart/form-data" } }
            );
            const bits = [
                data.updated?.episodes ? `${data.updated.episodes} episodes` : null,
                data.updated?.tracks ? `${data.updated.tracks} tracks` : null,
            ].filter(Boolean).join(" · ");
            toast.success(`Cover applied to ${bits || "0 items"}. Site refreshed.`);
            await loadHistory();
            onDone && (await onDone());
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth && onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Upload failed.");
            setPreview(null);
        } finally {
            setUploading(false);
        }
    };

    const rollback = async (item) => {
        setApplyingId(item.id);
        try {
            const { data } = await axios.post(
                `${API}/admin/covers/${item.id}/apply?scope=${scope}`,
                {},
                { headers }
            );
            const bits = [
                data.updated?.episodes ? `${data.updated.episodes} episodes` : null,
                data.updated?.tracks ? `${data.updated.tracks} tracks` : null,
            ].filter(Boolean).join(" · ");
            toast.success(`Rolled back to previous cover · ${bits || "no changes"}.`);
            onDone && (await onDone());
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth && onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Couldn't apply.");
        } finally {
            setApplyingId(null);
        }
    };

    const scheduleSwap = async () => {
        if (!schedCoverId) {
            toast.error("Pick a cover from history first.");
            return;
        }
        if (!schedWhen) {
            toast.error("Pick a date and time.");
            return;
        }
        // datetime-local returns "YYYY-MM-DDTHH:mm" in local time — convert to ISO.
        const isoLocal = new Date(schedWhen).toISOString();
        setSchedSaving(true);
        try {
            await axios.post(
                `${API}/admin/covers/${schedCoverId}/schedule`,
                { scheduled_for: isoLocal, scope },
                { headers }
            );
            toast.success("Cover swap scheduled.");
            setSchedWhen("");
            setSchedCoverId("");
            await loadSchedules();
            await loadHistory();
            onDone && (await onDone());
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth && onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Couldn't schedule.");
        } finally {
            setSchedSaving(false);
        }
    };

    const cancelSchedule = async (id) => {
        if (!window.confirm("Cancel this scheduled cover swap?")) return;
        try {
            await axios.delete(`${API}/admin/covers/schedule/${id}`, { headers });
            toast.success("Schedule cancelled.");
            await loadSchedules();
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth && onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Couldn't cancel.");
        }
    };

    const formatSchedTime = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString(undefined, {
                month: "short", day: "numeric",
                hour: "numeric", minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    const relativeTime = (iso) => {
        if (!iso) return "";
        const then = new Date(iso).getTime();
        const diff = Math.max(0, Date.now() - then);
        const m = Math.floor(diff / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
    };

    return (
        <div
            data-testid="admin-cover-uploader"
            className="rounded-3xl border border-lilac-200 bg-gradient-to-br from-[#FAF5FC] via-white to-[#FFF7E5] p-6 mb-6"
        >
            <div className="flex items-center gap-2 eyebrow text-lilac-900">
                <ImagePlus className="w-3.5 h-3.5" /> Swap cover art (no redeploy)
            </div>
            <p className="mt-2 text-sm text-lilac-700 leading-relaxed">
                Drop a new cover PNG/JPG/WebP and it replaces {scopeLabel} instantly.
                Older covers stay stored so you can revert anytime. 12 MB max.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                {["all", "podcast", "music"].map((k) => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setScope(k)}
                        data-testid={`admin-cover-scope-${k}`}
                        className={`text-xs rounded-full px-3 h-8 border transition-colors ${
                            scope === k
                                ? "bg-lilac-900 text-white border-lilac-900"
                                : "bg-white text-lilac-900 border-lilac-200 hover:border-lilac-900"
                        }`}
                    >
                        {k === "all" ? "All covers" : k === "podcast" ? "Episodes only" : "Music only"}
                    </button>
                ))}

                <Button
                    type="button"
                    onClick={chooseFile}
                    disabled={uploading}
                    data-testid="admin-cover-upload-btn"
                    className="ml-auto rounded-full h-10 px-5 bg-gold hover:bg-gold-deep text-white whitespace-nowrap"
                >
                    {uploading ? "Uploading…" : (
                        <>
                            <ImagePlus className="w-3.5 h-3.5" /> Choose image
                        </>
                    )}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={upload}
                    className="hidden"
                    data-testid="admin-cover-file-input"
                />
            </div>

            {preview && (
                <div className="mt-4 flex items-center gap-3">
                    <img
                        src={preview}
                        alt="Preview"
                        className="w-16 h-16 rounded-xl object-cover border border-lilac-200"
                    />
                    <span className="text-xs text-lilac-700">
                        {uploading ? "Uploading…" : "Applied · reloading list"}
                    </span>
                </div>
            )}

            {history.length > 0 && (
                <div className="mt-5 pt-5 border-t border-lilac-200/70">
                    <div className="flex items-center gap-2 mb-3">
                        <RotateCcw className="w-3.5 h-3.5 text-lilac-700" />
                        <span className="eyebrow text-lilac-700 text-[10px]">
                            History · roll back to a prior cover
                        </span>
                    </div>
                    <div
                        className="flex flex-wrap gap-3"
                        data-testid="admin-cover-history"
                    >
                        {history.map((item) => {
                            const busy = applyingId === item.id;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => rollback(item)}
                                    disabled={!!applyingId}
                                    data-testid={`admin-cover-history-${item.id}`}
                                    title={`Uploaded ${relativeTime(item.uploaded_at)} · scope: ${item.scope}`}
                                    className="group relative rounded-xl border border-lilac-200 bg-white overflow-hidden hover:border-gold transition-colors disabled:opacity-60"
                                >
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${item.cover_url}`}
                                        alt="Prior cover"
                                        className="w-16 h-16 object-cover"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-lilac-900/85 to-transparent text-[9px] font-medium text-white text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {busy ? "Applying…" : "Apply"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {history.length > 0 && (
                <div className="mt-5 pt-5 border-t border-lilac-200/70" data-testid="admin-cover-scheduler">
                    <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-3.5 h-3.5 text-lilac-700" />
                        <span className="eyebrow text-lilac-700 text-[10px]">
                            Schedule a cover swap · perfect for Season 2 or holiday themes
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            value={schedCoverId}
                            onChange={(e) => setSchedCoverId(e.target.value)}
                            data-testid="admin-cover-sched-select"
                            className="h-10 rounded-full px-4 bg-white border border-lilac-200 text-sm text-lilac-900 focus:outline-none focus:border-lilac-900"
                        >
                            <option value="">Choose cover from history…</option>
                            {history.map((h, i) => (
                                <option key={h.id} value={h.id}>
                                    #{i + 1} · {h.filename || "cover"} ({relativeTime(h.uploaded_at)})
                                </option>
                            ))}
                        </select>
                        <input
                            type="datetime-local"
                            value={schedWhen}
                            onChange={(e) => setSchedWhen(e.target.value)}
                            data-testid="admin-cover-sched-when"
                            className="h-10 rounded-full px-4 bg-white border border-lilac-200 text-sm text-lilac-900 focus:outline-none focus:border-lilac-900"
                        />
                        <Button
                            type="button"
                            onClick={scheduleSwap}
                            disabled={schedSaving || !schedCoverId || !schedWhen}
                            data-testid="admin-cover-sched-save"
                            className="rounded-full h-10 px-5 bg-lilac-900 hover:bg-lilac-700 text-white whitespace-nowrap"
                        >
                            {schedSaving ? "Scheduling…" : "Schedule swap"}
                        </Button>
                    </div>

                    {schedules.length > 0 && (
                        <div className="mt-4 space-y-2" data-testid="admin-cover-sched-upcoming">
                            <div className="text-[10px] uppercase tracking-wider text-lilac-700 font-semibold">
                                Upcoming ({schedules.length})
                            </div>
                            {schedules.map((s) => (
                                <div
                                    key={s.id}
                                    data-testid={`admin-cover-sched-item-${s.id}`}
                                    className="flex items-center gap-3 rounded-xl bg-white/70 border border-lilac-200/70 p-2 pl-3"
                                >
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}/api/covers/${s.cover_id}`}
                                        alt=""
                                        className="w-10 h-10 rounded-lg object-cover border border-lilac-200"
                                    />
                                    <div className="flex-1 min-w-0 text-xs">
                                        <div className="font-medium text-lilac-900 truncate">
                                            {formatSchedTime(s.scheduled_for)}
                                        </div>
                                        <div className="text-lilac-700 truncate">
                                            Scope: {s.scope === "all" ? "all covers" : s.scope + " only"}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => cancelSchedule(s.id)}
                                        data-testid={`admin-cover-sched-cancel-${s.id}`}
                                        className="text-[11px] text-lilac-700 hover:text-red-600 rounded-full px-3 h-7 border border-lilac-200 hover:border-red-300"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// ------------------------- Season 2 Seeder ------------------------------ //
// One-tap admin button that populates the 8 Season 2 placeholder episodes
// (numbers 7-14) in whichever MongoDB the backend is attached to. Idempotent
// — safe to click on production; existing episode rows are never overwritten.
const Season2Seeder = ({ headers, onDone, onUnauth }) => {
    const [seeding, setSeeding] = useState(false);
    const [result, setResult] = useState(null);

    const seed = async () => {
        if (!window.confirm(
            "Seed 8 Season 2 placeholder episodes (Ep 07–14) into this environment's database? " +
            "Safe to run — existing episodes will not be overwritten."
        )) return;
        setSeeding(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/seed/season2`,
                { weeks_out: 6, biweekly: true },
                { headers }
            );
            setResult(data);
            const c = (data.created || []).length;
            const e = (data.already_existed || []).length;
            if (c === 0) {
                toast.info(`Season 2 already seeded (${e} episodes existed).`);
            } else {
                toast.success(`Seeded ${c} Season 2 episode${c === 1 ? "" : "s"}. Ep 07 drops ${new Date(data.launch_at).toLocaleDateString()}.`);
            }
            onDone && (await onDone());
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth && onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Seeding failed.");
        } finally {
            setSeeding(false);
        }
    };

    return (
        <div
            data-testid="admin-s2-seeder"
            className="rounded-3xl border border-lilac-200 bg-gradient-to-br from-[#F7EFDA] via-white to-[#FAF5FC] p-6 mb-6"
        >
            <div className="flex items-center gap-2 eyebrow text-lilac-900">
                <Sparkles className="w-3.5 h-3.5" /> Season 2 · seed placeholders
            </div>
            <p className="mt-2 text-sm text-lilac-700 leading-relaxed">
                Insert Episodes 07 – 14 with bi-weekly release dates (Ep 07 drops ~6 weeks from today).
                Safe to run in production — existing episodes are left untouched.
                Only rows that do not already exist will be created.
            </p>

            <div className="mt-4 flex items-center gap-2">
                <Button
                    type="button"
                    onClick={seed}
                    disabled={seeding}
                    data-testid="admin-s2-seed-btn"
                    className="rounded-full h-10 px-5 bg-lilac-900 hover:bg-lilac-700 text-white whitespace-nowrap"
                >
                    {seeding ? "Seeding…" : (
                        <>
                            <Sparkles className="w-3.5 h-3.5" /> Seed Season 2 placeholders
                        </>
                    )}
                </Button>
                {result && (
                    <span
                        className="text-xs text-lilac-700"
                        data-testid="admin-s2-seed-result"
                    >
                        Created {(result.created || []).length} · already existed {(result.already_existed || []).length}
                    </span>
                )}
            </div>
        </div>
    );
};


// ----------------------------- Episodes Admin --------------------------- //
const EpisodesAdmin = ({ headers, adminKey, onUnauth }) => {
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState(emptyEpisode);
    const [editingId, setEditingId] = useState(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [rssUrl, setRssUrl] = useState("");
    const [syncing, setSyncing] = useState(false);
    const fileInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/episodes`);
            setEpisodes(r.data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // pull current RSS URL from settings
        axios.get(`${API}/settings`).then((r) => setRssUrl(r.data?.rss_feed_url || "")).catch(() => {});
    }, []);

    const startEdit = (ep) => {
        setEditingId(ep.id);
        setDraft({
            number: ep.number,
            title: ep.title,
            description: ep.description,
            theme: ep.theme,
            duration: ep.duration || "",
            audio_url: ep.audio_url || "",
            cover_url: ep.cover_url || "",
            status: ep.status || (ep.audio_url ? "published" : "coming_soon"),
            release_at: isoToDateInput(ep.release_at),
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const resetForm = () => {
        setEditingId(null);
        setDraft({ ...emptyEpisode, number: (episodes[episodes.length - 1]?.number || 0) + 1 });
    };

    const save = async (e) => {
        e.preventDefault();
        if (!draft.title || !draft.description) {
            toast.error("Title and description are required.");
            return;
        }
        try {
            const payload = { ...draft, release_at: dateInputToIso(draft.release_at) };
            if (editingId) {
                await axios.patch(`${API}/admin/episodes/${editingId}`, payload, { headers });
                toast.success("Episode updated.");
            } else {
                await axios.post(`${API}/admin/episodes`, payload, { headers });
                toast.success("Episode created.");
            }
            await load();
            resetForm();
        } catch (err) {
            if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
            else toast.error("Couldn't save.");
        }
    };

    const remove = async (ep) => {
        if (!window.confirm(`Delete "${ep.title}"?`)) return;
        try {
            await axios.delete(`${API}/admin/episodes/${ep.id}`, { headers });
            toast.success("Episode deleted.");
            await load();        } catch { toast.error("Couldn't delete."); }
    };

    const sendLaunchEmail = async (ep) => {
        const already = !!ep.launch_email_sent_at;
        const defaultSubject = already
            ? `Ep ${String(ep.number).padStart(2, "0")} — fixed links inside 💜 Press play.`
            : `Episode ${String(ep.number).padStart(2, "0")} is LIVE — press play. 🎙️💜`;
        const intro = already
            ? `Launch email was already sent on ${new Date(ep.launch_email_sent_at).toLocaleString()}.\n\nThis will RE-SEND to every subscriber. Enter a subject line (or press OK to accept the default):`
            : `Send the "${ep.title}" launch email to every subscriber.\n\nEnter a subject line (or press OK to accept the default):`;
        const subject = window.prompt(intro, defaultSubject);
        if (subject === null) return; // user cancelled
        const trimmed = subject.trim();
        if (!trimmed) {
            toast.error("Subject line can't be empty.");
            return;
        }
        try {
            const params = new URLSearchParams();
            if (already) params.set("force", "true");
            params.set("subject", trimmed);
            const url = `${API}/admin/episodes/${ep.id}/send-launch-email?${params.toString()}`;
            const { data } = await axios.post(url, {}, { headers });
            if (data?.ok === false && data?.reason === "already_sent") {
                toast.info("Already sent. Nothing dispatched.");
            } else {
                toast.success(`Launch email sent to ${data?.count ?? 0} subscribers.`);
            }
            await load();
        } catch (err) {
            if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
            else toast.error("Launch email dispatch failed.");
        }
    };

    const uploadAudio = async (file) => {
        if (!editingId) {
            toast.error("Save the episode first, then upload audio.");
            return;
        }
        if (!file) return;
        if (file.size > 100 * 1024 * 1024) {
            toast.error("Audio file is over 100 MB. Compress to MP3 first.");
            return;
        }
        setUploading(true);
        setUploadPct(0);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await axios.post(
                `${API}/admin/episodes/${editingId}/audio`,
                form,
                {
                    headers: { ...headers, "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e) => {
                        if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
                    },
                }
            );
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`;
            setDraft((d) => ({ ...d, audio_url: fullUrl, status: "published" }));
            toast.success("Audio uploaded. Episode is live.");
            await load();
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
            setUploadPct(0);
        }
    };

    const removeAudio = async () => {
        if (!editingId) return;
        if (!window.confirm("Remove the uploaded audio? Episode will revert to 'Coming Soon'.")) return;
        try {
            await axios.delete(`${API}/admin/episodes/${editingId}/audio`, { headers });
            setDraft((d) => ({ ...d, audio_url: "", status: "coming_soon" }));
            toast.success("Audio removed.");
            await load();
        } catch { toast.error("Couldn't remove audio."); }
    };

    const syncRss = async () => {
        if (!rssUrl) {
            toast.error("Paste your RSS feed URL first.");
            return;
        }
        setSyncing(true);
        try {
            // Persist the RSS URL into settings
            await axios.patch(`${API}/admin/settings`, { rss_feed_url: rssUrl }, { headers });
            const { data } = await axios.post(
                `${API}/admin/episodes/sync-rss`,
                { feed_url: rssUrl },
                { headers }
            );
            toast.success(`Synced: ${data.created} new, ${data.updated} updated.`);
            await load();
        } catch (err) {
            if (err?.response?.status === 401) { onUnauth(); }
            else toast.error(err?.response?.data?.detail || "Sync failed.");
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <section className="lg:col-span-2">
                <div className="rounded-3xl border border-border bg-white p-7 sticky top-32">
                    <div className="flex items-center gap-2 eyebrow text-gold-deep">
                        <Mic className="w-4 h-4" />
                        {editingId ? "Edit episode" : "New episode"}
                    </div>
                    <form onSubmit={save} data-testid="admin-episode-form" className="mt-5 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="eyebrow text-lilac-700">Episode #</label>
                                <Input type="number" min={1} value={draft.number}
                                    onChange={(e) => setDraft({ ...draft, number: Number(e.target.value) })}
                                    data-testid="admin-input-number" className="mt-2 h-10 rounded-xl" />
                            </div>
                            <div>
                                <label className="eyebrow text-lilac-700">Duration</label>
                                <Input placeholder="32:14" value={draft.duration}
                                    onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
                                    data-testid="admin-input-duration" className="mt-2 h-10 rounded-xl" />
                            </div>
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Release Date</label>
                            <Input
                                type="date"
                                value={draft.release_at || ""}
                                onChange={(e) => setDraft({ ...draft, release_at: e.target.value })}
                                data-testid="admin-input-release-date"
                                className="mt-2 h-10 rounded-xl"
                            />
                            <p className="mt-1.5 text-[11px] text-lilac-700/80 italic">
                                Drops at 12:00 AM Eastern on this date. Leave blank to release immediately.
                            </p>
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Title</label>
                            <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                                data-testid="admin-input-title" className="mt-2 h-10 rounded-xl" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Theme</label>
                            <Input value={draft.theme} onChange={(e) => setDraft({ ...draft, theme: e.target.value })}
                                data-testid="admin-input-theme" className="mt-2 h-10 rounded-xl"
                                placeholder="Break Toxic Cycles" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Description</label>
                            <textarea value={draft.description}
                                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                data-testid="admin-input-description" rows={4}
                                className="mt-2 w-full rounded-2xl border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Audio</label>
                            <Input value={draft.audio_url}
                                onChange={(e) => setDraft({ ...draft, audio_url: e.target.value })}
                                data-testid="admin-input-audio" className="mt-2 h-10 rounded-xl"
                                placeholder="Paste a URL or upload below" />
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!editingId) {
                                            toast.error("Click Edit on an episode first, then upload audio.");
                                            return;
                                        }
                                        if (uploading) return;
                                        fileInputRef.current?.click();
                                    }}
                                    data-testid="admin-audio-upload-btn"
                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                                        uploading
                                            ? "bg-lilac-100 text-lilac-700/70 cursor-wait"
                                            : !editingId
                                            ? "bg-lilac-100 text-lilac-900 hover:bg-lilac-200 cursor-pointer"
                                            : "bg-gold text-white hover:bg-gold-deep cursor-pointer"
                                    }`}
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    {uploading ? `Uploading ${uploadPct}%` : "Upload audio file"}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="audio/*,.mp3,.m4a,.wav,.aac"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) uploadAudio(f);
                                        e.target.value = ""; // allow re-picking same file
                                    }}
                                    className="hidden"
                                />
                                {draft.audio_url && draft.audio_url.includes("/api/episodes/") && (
                                    <button
                                        type="button"
                                        onClick={removeAudio}
                                        data-testid="admin-audio-remove"
                                        className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs uppercase tracking-wider bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Remove
                                    </button>
                                )}
                            </div>
                            <p className="mt-2 text-xs text-lilac-700/80">
                                {editingId
                                    ? "Drop an MP3/M4A (up to 100 MB) or paste an external URL. Empty = Coming Soon."
                                    : "Click Edit on an episode first, then upload audio."}
                            </p>
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Cover URL (optional)</label>
                            <Input value={draft.cover_url}
                                onChange={(e) => setDraft({ ...draft, cover_url: e.target.value })}
                                data-testid="admin-input-cover" className="mt-2 h-10 rounded-xl" />
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                            <Button type="submit" data-testid="admin-save"
                                className="rounded-full h-11 px-6 bg-gold hover:bg-gold-deep text-white">
                                <Save className="w-4 h-4" />{editingId ? "Update" : "Create"}
                            </Button>
                            {editingId && (
                                <button type="button" onClick={resetForm}
                                    className="text-sm text-lilac-700 hover:text-gold-deep">Cancel edit</button>
                            )}
                        </div>
                    </form>
                </div>
            </section>

            <section className="lg:col-span-3">
                {/* RSS auto-sync card */}
                <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] p-6 mb-6">
                    <div className="flex items-center gap-2 eyebrow text-gold-deep">
                        <Sparkles className="w-3.5 h-3.5" /> Auto-sync from Spotify / RSS
                    </div>
                    <p className="mt-2 text-sm text-lilac-700 leading-relaxed">
                        Paste your podcast RSS feed (Spotify for Creators → Settings → RSS Feed) and
                        click sync — every published episode appears here automatically.
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                        <Input
                            value={rssUrl}
                            onChange={(e) => setRssUrl(e.target.value)}
                            placeholder="https://anchor.fm/s/xxxxxxx/podcast/rss"
                            data-testid="admin-rss-input"
                            className="h-10 rounded-full bg-white"
                        />
                        <Button
                            type="button"
                            onClick={syncRss}
                            disabled={syncing || !rssUrl}
                            data-testid="admin-rss-sync"
                            className="rounded-full h-10 px-5 bg-gold hover:bg-gold-deep text-white whitespace-nowrap"
                        >
                            {syncing ? "Syncing…" : "Sync now"}
                        </Button>
                    </div>
                </div>

                {/* Cover-art uploader card (no redeploy needed) */}
                <CoverArtUploader headers={headers} onDone={load} onUnauth={onUnauth} />

                {/* One-tap Season 2 placeholder seed (idempotent) */}
                <Season2Seeder headers={headers} onDone={load} onUnauth={onUnauth} />

                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display text-3xl text-lilac-900">All episodes</h2>
                    <button onClick={resetForm} data-testid="admin-new-episode"
                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-4 h-10 bg-lilac-100 hover:bg-lilac-200 text-lilac-900">
                        <Plus className="w-4 h-4" /> New
                    </button>
                </div>
                {loading ? (
                    <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-2xl bg-lilac-100/50 animate-pulse" />))}</div>
                ) : (
                    <div className="space-y-3">{episodes.map((ep) => (
                        <div key={ep.id} data-testid={`admin-row-${ep.number}`}
                            className="flex items-center gap-4 rounded-2xl bg-white border border-border p-4">
                            <img src={ep.cover_url} alt="" className="w-16 h-16 rounded-xl object-cover border border-border" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="eyebrow text-lilac-700">EP {String(ep.number).padStart(2, "0")}</span>
                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                        ep.is_released && ep.audio_url ? "bg-green-100 text-green-800" :
                                        ep.is_released === false && ep.release_at ? "bg-lilac-100 text-lilac-900" :
                                        "bg-gold/15 text-gold-deep"
                                    }`}>{
                                        ep.is_released && ep.audio_url ? "Published" :
                                        ep.is_released === false && ep.release_at ? `Drops ${isoToDateInput(ep.release_at)}` :
                                        "Coming Soon"
                                    }</span>
                                </div>
                                <div className="font-display text-lg text-lilac-900 truncate">{ep.title}</div>
                                <div className="text-xs text-lilac-700 truncate">{ep.theme}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => sendLaunchEmail(ep)}
                                    data-testid={`admin-launch-email-${ep.number}`}
                                    aria-label={ep.launch_email_sent_at ? "Re-send launch email" : "Send launch email"}
                                    title={ep.launch_email_sent_at
                                        ? `Launch email sent ${new Date(ep.launch_email_sent_at).toLocaleString()} — click to re-send`
                                        : "Send launch email to all subscribers"}
                                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                        ep.launch_email_sent_at
                                            ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
                                            : "border-gold/40 bg-gold/10 text-gold-deep hover:bg-gold/20"
                                    }`}
                                >
                                    <Mail className="w-3.5 h-3.5" />
                                    {ep.launch_email_sent_at ? "Sent" : "Email"}
                                </button>
                                <button onClick={() => startEdit(ep)} data-testid={`admin-edit-${ep.number}`}
                                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-lilac-50 text-lilac-900">Edit</button>
                                <button onClick={() => remove(ep)} data-testid={`admin-delete-${ep.number}`}
                                    aria-label="Delete"
                                    className="grid place-items-center w-8 h-8 rounded-full border border-border text-lilac-700 hover:bg-red-50 hover:text-red-600">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>))}
                    </div>
                )}
            </section>
        </div>
    );
};

const MusicAdmin = ({ headers, onUnauth }) => {
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState(emptyTrack);
    const [editingId, setEditingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/music`);
            setTracks(r.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startEdit = (t) => {
        setEditingId(t.id);
        setDraft({
            title: t.title,
            description: t.description || "",
            cover_url: t.cover_url || "",
            apple_url: t.apple_url || "",
            spotify_url: t.spotify_url || "",
            youtube_url: t.youtube_url || "",
            track_type: t.track_type || "Album",
            order: t.order || 1,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const resetForm = () => {
        setEditingId(null);
        setDraft({ ...emptyTrack, order: (tracks[tracks.length - 1]?.order || 0) + 1 });
    };

    const save = async (e) => {
        e.preventDefault();
        if (!draft.title) { toast.error("Title is required."); return; }
        try {
            if (editingId) {
                await axios.patch(`${API}/admin/music/${editingId}`, draft, { headers });
                toast.success("Track updated.");
            } else {
                await axios.post(`${API}/admin/music`, draft, { headers });
                toast.success("Track added.");
            }
            await load();
            resetForm();
        } catch (err) {
            if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
            else toast.error("Couldn't save.");
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete "${t.title}"?`)) return;
        try {
            await axios.delete(`${API}/admin/music/${t.id}`, { headers });
            toast.success("Track deleted.");
            await load();
        } catch { toast.error("Couldn't delete."); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <section className="lg:col-span-2">
                <div className="rounded-3xl border border-border bg-white p-7 sticky top-32">
                    <div className="flex items-center gap-2 eyebrow text-gold-deep">
                        <MusicIcon className="w-4 h-4" />
                        {editingId ? "Edit track" : "New track"}
                    </div>
                    <form onSubmit={save} data-testid="admin-music-form" className="mt-5 space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <label className="eyebrow text-lilac-700">Title</label>
                                <Input value={draft.title}
                                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                                    data-testid="music-input-title" className="mt-2 h-10 rounded-xl" />
                            </div>
                            <div>
                                <label className="eyebrow text-lilac-700">Order</label>
                                <Input type="number" min={1} value={draft.order}
                                    onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
                                    data-testid="music-input-order" className="mt-2 h-10 rounded-xl" />
                            </div>
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Description</label>
                            <textarea value={draft.description}
                                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                data-testid="music-input-description" rows={3}
                                className="mt-2 w-full rounded-2xl border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Type</label>
                            <Input
                                value={draft.track_type}
                                onChange={(e) => setDraft({ ...draft, track_type: e.target.value })}
                                data-testid="music-input-type"
                                className="mt-2 h-10 rounded-xl"
                                placeholder="Album · Single · EP · In Studio"
                            />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Cover URL</label>
                            <Input value={draft.cover_url}
                                onChange={(e) => setDraft({ ...draft, cover_url: e.target.value })}
                                data-testid="music-input-cover" className="mt-2 h-10 rounded-xl"
                                placeholder="https://… (album art)" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Spotify URL</label>
                            <Input value={draft.spotify_url}
                                onChange={(e) => setDraft({ ...draft, spotify_url: e.target.value })}
                                data-testid="music-input-spotify" className="mt-2 h-10 rounded-xl"
                                placeholder="https://open.spotify.com/track/…" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">Apple Music URL</label>
                            <Input value={draft.apple_url}
                                onChange={(e) => setDraft({ ...draft, apple_url: e.target.value })}
                                data-testid="music-input-apple" className="mt-2 h-10 rounded-xl"
                                placeholder="https://music.apple.com/…" />
                        </div>
                        <div>
                            <label className="eyebrow text-lilac-700">YouTube URL (optional)</label>
                            <Input value={draft.youtube_url}
                                onChange={(e) => setDraft({ ...draft, youtube_url: e.target.value })}
                                data-testid="music-input-youtube" className="mt-2 h-10 rounded-xl"
                                placeholder="https://youtube.com/…" />
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                            <Button type="submit" data-testid="music-save"
                                className="rounded-full h-11 px-6 bg-gold hover:bg-gold-deep text-white">
                                <Save className="w-4 h-4" />{editingId ? "Update" : "Add"}
                            </Button>
                            {editingId && (
                                <button type="button" onClick={resetForm}
                                    className="text-sm text-lilac-700 hover:text-gold-deep">Cancel edit</button>
                            )}
                        </div>
                    </form>
                </div>
            </section>

            <section className="lg:col-span-3">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display text-3xl text-lilac-900">All tracks</h2>
                    <button onClick={resetForm} data-testid="admin-new-track"
                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-4 h-10 bg-lilac-100 hover:bg-lilac-200 text-lilac-900">
                        <Plus className="w-4 h-4" /> New
                    </button>
                </div>
                {loading ? (
                    <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-2xl bg-lilac-100/50 animate-pulse" />))}</div>
                ) : (
                    <div className="space-y-3">{tracks.map((t) => (
                        <div key={t.id} data-testid={`music-row-${t.id}`}
                            className="flex items-center gap-4 rounded-2xl bg-white border border-border p-4">
                            <img src={t.cover_url} alt="" className="w-16 h-16 rounded-xl object-cover border border-border" />
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-lg text-lilac-900 truncate">{t.title}</div>
                                <div className="text-xs text-lilac-700 truncate">
                                    {[t.spotify_url && "Spotify", t.apple_url && "Apple", t.youtube_url && "YouTube"]
                                        .filter(Boolean).join(" · ") || "No links yet"}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => startEdit(t)} data-testid={`music-edit-${t.id}`}
                                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-lilac-50 text-lilac-900">Edit</button>
                                <button onClick={() => remove(t)} data-testid={`music-delete-${t.id}`}
                                    aria-label="Delete"
                                    className="grid place-items-center w-8 h-8 rounded-full border border-border text-lilac-700 hover:bg-red-50 hover:text-red-600">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>))}
                    </div>
                )}
            </section>
        </div>
    );
};

// ----------------------------- Prayers Admin --------------------------- //
const PrayersAdmin = ({ headers, onUnauth }) => {
    const [prayers, setPrayers] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/prayers`, { headers });
            setPrayers(r.data || []);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired.");
                onUnauth();
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (p) => {
        if (!window.confirm("Delete this prayer?")) return;
        try {
            await axios.delete(`${API}/admin/prayers/${p.id}`, { headers });
            toast.success("Prayer removed.");
            await load();
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired.");
                onUnauth();
            } else {
                toast.error("Couldn't delete.");
            }
        }
    };

    const fmt = (iso) => {
        try { return new Date(iso).toLocaleString(); } catch { return ""; }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-3xl text-lilac-900">Prayer requests</h2>
                <span className="eyebrow text-lilac-700" data-testid="prayer-count">
                    {prayers.length} {prayers.length === 1 ? "prayer" : "prayers"}
                </span>
            </div>
            {loading ? (
                <div className="space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-28 rounded-2xl bg-lilac-100/50 animate-pulse" />
                    ))}
                </div>
            ) : prayers.length === 0 ? (
                <div className="rounded-3xl border border-border bg-white p-10 text-center">
                    <HandHeart className="w-8 h-8 text-gold-deep mx-auto" />
                    <p className="mt-4 text-lilac-700">
                        No prayers yet. They'll appear here as soon as someone shares one.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {prayers.map((p) => {
                        const display = (p.is_anonymous || !p.name) ? "Anonymous" : p.name;
                        return (
                            <article
                                key={p.id}
                                data-testid={`admin-prayer-${p.id}`}
                                className="rounded-2xl bg-white border border-border p-5 flex flex-col"
                            >
                                <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-lilac-700">
                                    <span>{display}</span>
                                    <span>{fmt(p.created_at)}</span>
                                </div>
                                <p className="mt-3 font-display italic text-lilac-900 text-base leading-snug whitespace-pre-wrap">
                                    "{p.prayer}"
                                </p>
                                <div className="mt-4 flex justify-end">
                                    <button
                                        onClick={() => remove(p)}
                                        data-testid={`admin-prayer-delete-${p.id}`}
                                        aria-label="Delete prayer"
                                        className="grid place-items-center w-8 h-8 rounded-full border border-border text-lilac-700 hover:bg-red-50 hover:text-red-600"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AdminPage;

// ---------------------------- Subscribers ------------------------------- //
const SubscribersAdmin = ({ headers, adminKey, onUnauth }) => {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/admin/subscribers`, { headers });
            setRows(r.data || []);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired.");
                onUnauth();
            } else {
                toast.error("Couldn't load subscribers.");
            }
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const exportCSV = async () => {
        setBusy(true);
        try {
            const res = await fetch(`${API}/admin/subscribers.csv`, {
                headers: { "X-Admin-Key": adminKey },
            });
            if (!res.ok) {
                if (res.status === 401) {
                    onUnauth();
                    return;
                }
                throw new Error("Export failed");
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const today = new Date().toISOString().slice(0, 10);
            a.download = `becomingher-subscribers-${today}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded — ready for Beehiiv.");
        } catch (err) {
            toast.error("Couldn't export. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const copyEmails = async () => {
        if (!rows || !rows.length) {
            toast.error("No subscribers yet.");
            return;
        }
        try {
            await navigator.clipboard.writeText(rows.map((r) => r.email).join(", "));
            toast.success(`Copied ${rows.length} email${rows.length === 1 ? "" : "s"}.`);
        } catch (err) {
            toast.error("Clipboard not available.");
        }
    };

    if (rows === null) {
        return <div className="h-32 rounded-3xl bg-lilac-100/50 animate-pulse" />;
    }

    const founding = rows.filter((r) => r.is_founding).length;

    return (
        <div className="space-y-8" data-testid="subscribers-admin">
            {/* Header + actions */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="eyebrow text-gold-deep">Subscribers</div>
                    <h2 className="font-display text-3xl text-lilac-900 mt-1">
                        The list. ({rows.length})
                    </h2>
                    <p className="text-sm text-lilac-700 mt-2 max-w-xl leading-relaxed">
                        Real subscribers who joined the waitlist on the site. Export to CSV to import
                        into Beehiiv or any newsletter tool.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={copyEmails}
                        disabled={!rows.length}
                        data-testid="subscribers-copy-emails"
                        className="rounded-full"
                    >
                        Copy emails
                    </Button>
                    <Button
                        type="button"
                        onClick={exportCSV}
                        disabled={!rows.length || busy}
                        data-testid="subscribers-export-csv"
                        className="rounded-full bg-gold hover:bg-gold-deep text-white"
                    >
                        <Download className="w-4 h-4 mr-1.5" />
                        {busy ? "Exporting…" : "Export CSV (Beehiiv)"}
                    </Button>
                </div>
            </div>

            {/* Beehiiv import helper */}
            <div className="rounded-3xl bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] border border-gold/30 p-6 md:p-7">
                <div className="flex items-center gap-2 eyebrow text-gold-deep">
                    <Sparkles className="w-3.5 h-3.5" /> Beehiiv import in 60 seconds
                </div>
                <ol className="mt-3 list-decimal pl-5 text-sm text-lilac-900 space-y-1.5 leading-relaxed">
                    <li>Click <strong>Export CSV</strong> above.</li>
                    <li>
                        In Beehiiv → <strong>Audience</strong> → <strong>Subscribers</strong> →{" "}
                        <strong>Import</strong>.
                    </li>
                    <li>Upload the CSV and map the <em>email</em> column. Skip the rest if you'd like.</li>
                    <li>Beehiiv will dedupe and welcome them with your Beehiiv automation.</li>
                </ol>
                <p className="mt-3 text-xs text-lilac-700 italic">
                    Tip: re-export weekly so new signups flow into Beehiiv without missing a beat.
                </p>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-3 gap-4">
                <div className="rounded-2xl bg-white border border-border p-5">
                    <div className="eyebrow text-lilac-700/80">Total</div>
                    <div className="font-sans font-semibold text-3xl text-lilac-900 mt-1 tabular-nums">
                        {rows.length}
                    </div>
                </div>
                <div className="rounded-2xl bg-white border border-gold/30 p-5">
                    <div className="eyebrow text-gold-deep">Founding 100</div>
                    <div className="font-sans font-semibold text-3xl text-gold-deep mt-1 tabular-nums">
                        {founding}
                    </div>
                </div>
                <div className="rounded-2xl bg-white border border-border p-5">
                    <div className="eyebrow text-lilac-700/80">Seats left</div>
                    <div className="font-sans font-semibold text-3xl text-lilac-900 mt-1 tabular-nums">
                        {Math.max(0, 100 - founding)}
                    </div>
                </div>
            </div>

            {/* List */}
            {rows.length === 0 ? (
                <div className="rounded-3xl bg-white border border-dashed border-lilac-200 p-10 text-center">
                    <Mail className="w-6 h-6 text-lilac-700 mx-auto" />
                    <p className="mt-3 text-lilac-700">No subscribers yet — share your link and watch the seats fill. 💜</p>
                </div>
            ) : (
                <div className="rounded-3xl bg-white border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-lilac-100/60 text-lilac-900">
                            <tr>
                                <th className="text-left px-5 py-3 eyebrow">Email</th>
                                <th className="text-left px-5 py-3 eyebrow">Joined</th>
                                <th className="text-left px-5 py-3 eyebrow">Seat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr
                                    key={r.id || r.email}
                                    data-testid="subscriber-row"
                                    className="border-t border-lilac-200/60"
                                >
                                    <td className="px-5 py-3 text-lilac-900 break-all">{r.email}</td>
                                    <td className="px-5 py-3 text-lilac-700 whitespace-nowrap">
                                        {r.subscribed_at
                                            ? new Date(r.subscribed_at).toLocaleDateString(undefined, {
                                                  year: "numeric",
                                                  month: "short",
                                                  day: "numeric",
                                              })
                                            : "—"}
                                    </td>
                                    <td className="px-5 py-3 whitespace-nowrap">
                                        {r.is_founding && r.position ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/15 border border-gold/40 text-gold-deep text-[11px] font-bold">
                                                #{r.position} · Founding
                                            </span>
                                        ) : (
                                            <span className="text-lilac-700/70 text-xs">Subscriber</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
